
import { ChatMessage } from './ChatMessage';
import { useTranslation } from 'react-i18next';
import { useRef, memo } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { EASE_OUT } from '@/lib/ease';
import { MessageScroller } from '@/components/agents/message-scroller';
import { ThinkingShimmer } from '@/components/agents/loading-states/thinking-shimmer';
import { ChatPanelNote } from './chat/ChatPanelNote';

interface ChatConversationProps {
    conversation: any;
    pendingRequest: boolean;
    submitInput: (input: string, prompt?: string, options?: { exclusiveToolId?: string; onAccepted?: () => void }) => void;
    onFeedback: (index: number, isPositive: boolean) => void;
    feedbackByIndex?: Record<number, 'good' | 'bad'>;
    resourceSelected?: (resource: any) => void;
    /** Hide the per-message action bar (sources, feedback, copy) — see ChatMessage. */
    hideMessageActions?: boolean;
    /** Narrow bar: no thumbs and no secondary button; copy and sources remain — see ChatMessage. */
    compactActions?: boolean;
}

const ChatConversationComponent = ({
    conversation,
    pendingRequest,
    submitInput,
    onFeedback,
    feedbackByIndex,
    resourceSelected,
    hideMessageActions,
    compactActions,
}: ChatConversationProps) => {
    const { t } = useTranslation();

    // While the newest real message is a bot message that already shows its steps bar, that bar
    // carries the "Denke nach …" shimmer in place — a second indicator at the bottom would only
    // repeat it. Side notes (panelNote) are skipped: they are not a turn of the conversation.
    const stepsBarShowsThinking = (() => {
        if (!pendingRequest) return false;
        const messages: any[] = conversation?.messages ?? [];
        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i];
            if (message?.type === 'panelNote') continue;
            return message?.type === 'botMessage' && /<div\b[^>]*\sclass\s*=\s*["']progress["']/i.test(message?.content ?? '');
        }
        return false;
    })();
    const messagesEndRef = useRef(null) as any;

    /**
     * Eingeblendet wird nur, was *während* der Sitzung dazukommt.
     *
     * Beim Öffnen einer alten Unterhaltung stünde sonst der ganze Verlauf auf
     * einmal auf — und beim Scrollen wieder. Deshalb merkt sich die Liste den
     * Stand, mit dem eine Unterhaltung zum ersten Mal auf den Schirm kommt;
     * erst die Nachrichten dahinter tragen die Einblende-Animation von
     * `Message`/`MessageBubble` (`animateIn`). Der Wechsel der Unterhaltung
     * setzt die Marke neu — die Komponente wird dabei nicht neu gemountet.
     */
    const conversationId = conversation?._id != null ? String(conversation._id) : '';
    const messageCount = conversation?.messages?.length ?? 0;
    // Switching conversations fades the new transcript in with a slight rise (user decision
    // 28.09.2026). Keyed on the id, so streaming into the same conversation never re-triggers
    // it. Deliberately no exit phase: an `AnimatePresence mode="wait"` would hold the new
    // transcript back until the old one had faded — and the assistant panel swaps its
    // conversation id on every page jump. `motion-reduce` cuts.
    const reduce = useReducedMotion();
    const swapTransition = reduce ? { duration: 0 } : { duration: 0.18, ease: EASE_OUT };
    const revealedRef = useRef<{ conversationId: string; count: number } | null>(null);
    if (!revealedRef.current || revealedRef.current.conversationId !== conversationId) {
        revealedRef.current = { conversationId, count: messageCount };
    }
    const revealedBefore = revealedRef.current.count;

    /**
     * The last **conversation turn**, not the last entry.
     *
     * `isLastMessage` drives the streaming state in `ChatMessage`: only the
     * last message shows the running step, holds back its action bar and
     * announces itself via `aria-live`. A side note, however, is not a turn —
     * and the question panel appends one precisely while an answer is still
     * running (the „Ich bin mitgekommen" line after a jump out of its own
     * answer). If it counted, the streaming answer would lose its state
     * mid-write: copy button and sources would appear, the step group would
     * collapse — while the shimmer keeps running next to it.
     */
    const lastMessageIndex: number = (conversation?.messages ?? []).reduce(
        (last: number, message: any, i: number) => (message?.type === 'panelNote' ? last : i),
        -1,
    );

    /*
     * Der Verlauf ist der `MessageScroller` aus den beui-Primitiven: er hängt
     * beim Streamen an der Live-Kante und gibt sie frei, sobald die Leserin
     * selbst scrollt.
     *
     * Die drei alten Klassennamen leben auf den neuen Elementen weiter —
     * `.messagesContainer` auf der Wurzel, `.messages` auf dem tatsächlich
     * scrollenden Bereich, `.messagesList` auf der Nachrichtenspalte. Daran
     * hängen der Sprung-zum-Ende-Knopf (`ScrollToBottomButton`), das
     * Rad-Weiterreichen aus den Rändern (`ChatBot`) und die Alt-Regeln in
     * `App.sass`/`chat-oa.css`.
     *
     * `justify-end` auf dem Inhalt hält die alte Eigenart bei: ein kurzer
     * Verlauf steht unten, der Freiraum oben.
     */
    return (
        <MessageScroller
            // `h-full` greift nur dort, wo keine Alt-Regel eine Höhe setzt
            // (Ausschreibungs-Board, Testdialog) — im Chat gewinnt die
            // spezifischere Regel aus `chat-oa.css`.
            className="messagesContainer h-full"
            viewportClassName="messages [scrollbar-gutter:auto]"
            contentClassName="flex min-h-full flex-col justify-end"
            label={t('chatMessages.conversationRegion')}
            busy={pendingRequest}
            // Opening a conversation shows its end right away (user decision 29.09.2026):
            // the scroller positions the new transcript before its first paint, so the
            // fade-in below reveals the bottom, not the top. Empty until messages are
            // there, so a conversation that loads its history late opens at the end too.
            openKey={messageCount > 0 ? conversationId || 'empty' : ''}
        >
            <motion.div
                // Also re-keyed when the history arrives: right after sign-in the
                // conversation is chosen (id set) before its messages load, and
                // without this the transcript faded in empty and the history then
                // popped in a second later.
                key={`${conversationId || 'empty'}:${messageCount > 0 ? 'history' : 'blank'}`}
                initial={reduce ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={swapTransition}
                className="flex min-h-full flex-col justify-end"
                data-testid="chat-transcript"
                data-conversation-id={conversationId}
            >
            {conversation && conversation.messages && conversation.messages.length > 0
                ?
                <>
                    <div className="messagesList">
                    {conversation.messages.map((message: any, index: number) => (
                        // A side note is not a conversation turn: it stands
                        // between the messages, without bubble and without
                        // actions. Today only the question panel emits such
                        // lines — the server does not know the type.
                        // Keyed by the message, not by its position: the
                        // question panel prepends an adopted history at the
                        // *front*, and with `key={index}` the state of a line
                        // (expanded question, source list) would then leak
                        // into another message. Where no `id` comes along —
                        // the main chat has none — it stays on the index as
                        // before.
                        message?.type === 'panelNote'
                            ? <ChatPanelNote key={message.id ?? index} testId={message.testId}>
                                {message.content}
                            </ChatPanelNote>
                            : <ChatMessage
                                key={message?.id ?? index}
                                message={message}
                                index={index}
                                isLastMessage={index === lastMessageIndex}
                                pendingRequest={pendingRequest}
                                animateIn={index >= revealedBefore}
                                onSubmitInput={submitInput}
                                onFeedback={onFeedback}
                                feedbackValue={feedbackByIndex?.[index]}
                                resourceSelected={resourceSelected}
                                hideMessageActions={hideMessageActions}
                                compactActions={compactActions}
                            />
                    ))}
                    </div>
                    {pendingRequest && !stepsBarShowsThinking ?
                        // Denk-Anzeige in der Zeile der Antworten (kein Avatar,
                        // keine Sprechblase): der Schimmer läuft über das Wort,
                        // sonst nichts.
                        <div
                            className="my-1.5 px-[30px] py-0.5 max-sm:px-1"
                            data-progress-indicator="true"
                            data-testid="chat-pending-indicator"
                        >
                            <div className="flex h-8 min-w-0 items-center text-[15px]">
                                {/* Fest „Denke nach …“ — die wechselnden Begriffe gehören zur
                                    Arbeit, sie kommen erst mit den ersten Schritten
                                    (`ChatAgentSteps`). */}
                                <ThinkingShimmer className="font-normal">
                                    {t('chatMessages.thinking')}
                                </ThinkingShimmer>
                            </div>
                        </div>
                        :
                        <></>}
                    <div ref={messagesEndRef}></div>
                </>
                :
                <></>
            }
            </motion.div>
        </MessageScroller>
    );
};

// Memoized to prevent re-renders on every keystroke in the parent ChatExchange component.
// During streaming, ChatExchange calls setConversation with a new cloned object,
// which triggers this component to re-render as expected.
export const ChatConversation = memo(ChatConversationComponent);
